// Tenant currency helpers (SPEC-growchat-tenant-currency.md, Design 8).
// Parsing never guesses: any input not matching a listed shape is an error.

import * as Sentry from '@sentry/react'

export const DEFAULT_CURRENCY = 'VND'

const CURRENCY_RULES = {
  VND: { minorUnits: 0, maxAmount: 100_000_000_000 },
  CAD: { minorUnits: 2, maxAmount: 100_000_000 },
  USD: { minorUnits: 2, maxAmount: 100_000_000 },
}

export const SUPPORTED_CURRENCIES = Object.freeze(Object.keys(CURRENCY_RULES))

const UI_LOCALE_TO_INTL = { vi: 'vi-VN', en: 'en-CA' }
const DEFAULT_INTL_LOCALE = 'en-CA'

export const MONEY_INPUT_ERROR = 'invalid_money_format'
export const UNSUPPORTED_CURRENCY_ERROR = 'unsupported_currency'
export const AMOUNT_TOO_LARGE_ERROR = 'amount_too_large'

// Amounts are decimal numbers in major units. Do NOT convert with `* 100`
// integer-cents math (float error, e.g. 4.35 * 100 = 434.99999999999994).

// Fallbacks for codes outside SUPPORTED_CURRENCIES. The cap is the largest
// supported cap (VND) so an unknown currency is never blocked more strictly.
const FALLBACK_MAX_AMOUNT = CURRENCY_RULES.VND.maxAmount
const FALLBACK_INVALID_MINOR_UNITS = 0

const ISO_CODE = /^[A-Z]{3}$/

// Classify a currency: `supported`, `iso` (valid ISO code Intl accepts but we
// do not support) or `invalid`. Absent reads as VND (backend default).
function classifyCurrency(currency) {
  if (currency === null || currency === undefined || currency === '') {
    return { kind: 'supported', code: DEFAULT_CURRENCY }
  }
  if (typeof currency !== 'string') return { kind: 'invalid', code: '' }
  const code = currency.trim().toUpperCase()
  if (CURRENCY_RULES[code]) return { kind: 'supported', code }
  if (ISO_CODE.test(code) && intlDefaultMinorUnits(code) !== null) return { kind: 'iso', code }
  return { kind: 'invalid', code }
}

/** Intl's default fraction digits for an ISO code, or null if Intl rejects it. */
function intlDefaultMinorUnits(code) {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency: code })
      .resolvedOptions().maximumFractionDigits
  } catch {
    return null
  }
}

const reportedCurrencies = new Set()
const MAX_REPORTED_CURRENCIES = 50

/** Report an unsupported currency to Sentry once per code per session. */
function reportUnsupportedCurrency(code) {
  if (!code || reportedCurrencies.has(code)) return
  if (reportedCurrencies.size >= MAX_REPORTED_CURRENCIES) return
  reportedCurrencies.add(code)
  try {
    Sentry.captureMessage(`formatMoney: unsupported currency "${code}"`, {
      level: 'warning',
      tags: { action: 'format_money' },
    })
  } catch {
    /* reporting must never break rendering */
  }
}

/** Decimals: VND 0, CAD/USD 2; unsupported ISO -> Intl default; invalid -> 0. */
export function getMinorUnits(currency) {
  const { kind, code } = classifyCurrency(currency)
  if (kind === 'supported') return CURRENCY_RULES[code].minorUnits
  if (kind === 'iso') return intlDefaultMinorUnits(code)
  return FALLBACK_INVALID_MINOR_UNITS
}

/** Per-price-field max; unsupported or invalid codes get the VND (largest) cap. */
export function getMaxAmount(currency) {
  const { kind, code } = classifyCurrency(currency)
  return kind === 'supported' ? CURRENCY_RULES[code].maxAmount : FALLBACK_MAX_AMOUNT
}

/** Canonical input text for an amount: `4.50` (CAD/USD), `1,250,000` (VND); '' when absent or invalid. */
export function formatAmountForInput(value, currency) {
  if (value === null || value === undefined || value === '') return ''
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) return ''
  const digits = getMinorUnits(currency)
  // `en-US` grouping (`,` groups, `.` decimal) is a shape parseMoneyInput accepts.
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(parsed)
}

/** Map the app UI locale (`vi` / `en`) to the Intl locale used for money. */
function toIntlLocale(uiLocale) {
  const base = typeof uiLocale === 'string' ? uiLocale.trim().toLowerCase().split(/[-_]/)[0] : ''
  return UI_LOCALE_TO_INTL[base] ?? DEFAULT_INTL_LOCALE
}

/**
 * Format for display; never throws (renders inside React). Supported: exact
 * minor units. Unsupported ISO: Intl default digits. Invalid code: grouped
 * number + " " + code. Missing or non-finite amount -> ''.
 */
export function formatMoney(amount, currency, uiLocale) {
  const isNumeric = typeof amount === 'number' || (typeof amount === 'string' && amount.trim() !== '')
  if (!isNumeric) return ''
  const value = Number(amount)
  if (!Number.isFinite(value)) return ''
  const locale = toIntlLocale(uiLocale)
  const { kind, code } = classifyCurrency(currency)
  try {
    if (kind === 'invalid') {
      reportUnsupportedCurrency(code)
      const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)
      return code ? `${number} ${code}` : number
    }
    if (kind === 'iso') {
      reportUnsupportedCurrency(code)
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(value)
    }
    const digits = CURRENCY_RULES[code].minorUnits
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value)
  } catch {
    // Last-resort fallback; Intl should not throw for the inputs above.
    return `${value} ${code}`.trim()
  }
}

// Shapes shared by every currency.
const DIGITS_ONLY = /^\d+$/
// First group 1-3 digits without a leading zero, then groups of exactly 3
// digits joined by ONE repeated separator kind (captured as group 1).
const GROUPED_ONE_OR_MORE = /^[1-9]\d{0,2}([.,])\d{3}(?:\1\d{3})*$/

// CAD/USD shapes.
// (a) digits, one separator, then 1-2 decimal digits.
const DECIMAL_SIMPLE = /^(\d+)[.,](\d{1,2})$/
// (b) grouping only: the separator kind must repeat at least twice.
const GROUPED_TWO_OR_MORE = /^[1-9]\d{0,2}([.,])\d{3}(?:\1\d{3})+$/
// (c) grouping (one kind, any count >= 1), then the OTHER kind once as decimal.
const GROUPED_WITH_DECIMAL = /^([1-9]\d{0,2}([.,])\d{3}(?:\2\d{3})*)([.,])(\d{1,2})$/

const ok = (value) => ({ ok: true, value })
const fail = (error = MONEY_INPUT_ERROR) => ({ ok: false, error })

const stripSeparators = (text) => text.replace(/[.,]/g, '')

function parseTwoDecimalAmount(text) {
  if (DIGITS_ONLY.test(text)) return ok(Number(text))
  const simple = DECIMAL_SIMPLE.exec(text)
  if (simple) return ok(Number(`${simple[1]}.${simple[2]}`))
  if (GROUPED_TWO_OR_MORE.test(text)) return ok(Number(stripSeparators(text)))
  const mixed = GROUPED_WITH_DECIMAL.exec(text)
  if (mixed && mixed[2] !== mixed[3]) {
    return ok(Number(`${stripSeparators(mixed[1])}.${mixed[4]}`))
  }
  // Rejects ambiguous lone separator + 3 digits (`1,250`) and + 4+ digits.
  return fail()
}

function parseIntegerAmount(text) {
  if (DIGITS_ONLY.test(text)) return ok(Number(text))
  if (GROUPED_ONE_OR_MORE.test(text)) return ok(Number(stripSeparators(text)))
  return fail()
}

/**
 * Parse typed money input. Returns `{ok: true, value}` (value null when empty),
 * or `{ok: false, error}` with UNSUPPORTED_CURRENCY_ERROR, MONEY_INPUT_ERROR or
 * AMOUNT_TOO_LARGE_ERROR (unsafe/non-finite or above `getMaxAmount`). Never throws.
 */
export function parseMoneyInput(raw, currency) {
  const { kind, code } = classifyCurrency(currency)
  if (kind !== 'supported') return fail(UNSUPPORTED_CURRENCY_ERROR)
  if (raw === null || raw === undefined) return ok(null)
  if (typeof raw !== 'string') return fail()
  const text = raw.trim()
  if (text === '') return ok(null)
  const result = CURRENCY_RULES[code].minorUnits === 0
    ? parseIntegerAmount(text)
    : parseTwoDecimalAmount(text)
  if (!result.ok) return result
  const tooLarge = !Number.isFinite(result.value) || result.value > Number.MAX_SAFE_INTEGER
  return tooLarge || result.value > CURRENCY_RULES[code].maxAmount
    ? fail(AMOUNT_TOO_LARGE_ERROR)
    : result
}
